import { useState, useRef, useEffect } from 'react';
import { MapPin, ChevronDown, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import 'leaflet/dist/leaflet.css';

// Vancouver Island wine regions with coordinates
const regions = [
  { id: 'comox-valley',       name: 'Comox Valley',       tagline: 'Courtenay & Comox',                             lat: 49.680, lng: -124.970 },
  { id: 'denman-island',      name: 'Denman Island',      tagline: 'Ferry from Buckley Bay',                        lat: 49.495, lng: -124.820 },
  { id: 'hornby-island',      name: 'Hornby Island',      tagline: 'Ferry: Buckley Bay → Denman → Hornby',          lat: 49.545, lng: -124.640 },
  { id: 'central-island',     name: 'Central Island',     tagline: 'Nanaimo',                                       lat: 49.190, lng: -124.060 },
  { id: 'central-cowichan',   name: 'Central Cowichan',   tagline: 'Duncan & North Cowichan',                       lat: 48.790, lng: -123.820 },
  { id: 'south-cowichan',     name: 'South Cowichan',     tagline: 'Cobble Hill & Mill Bay',                        lat: 48.680, lng: -123.720 },
  { id: 'south-island',       name: 'South Island',       tagline: 'Saanich Peninsula & Victoria',                  lat: 48.580, lng: -123.480 },
  { id: 'salt-spring-island', name: 'Salt Spring Island', tagline: 'Ferry from Swartz Bay or Tsawwassen',           lat: 48.870, lng: -123.500 },
  { id: 'pender-island',      name: 'Pender Island',      tagline: 'Ferry from Swartz Bay or Tsawwassen',           lat: 48.778, lng: -123.270 },
  { id: 'saturna-island',     name: 'Saturna Island',     tagline: 'Ferry from Swartz Bay — most remote',           lat: 48.760, lng: -123.133 },
];

function RegionMap({ selectedId, onSelect }) {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current || leafletMapRef.current) return;

    import('leaflet').then(L => {
      const map = L.map(mapRef.current, {
        center: [49.150, -124.050],
        zoom: 8,
        zoomControl: false,
        attributionControl: false,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 14,
      }).addTo(map);

      L.control.zoom({ position: 'bottomright' }).addTo(map);

      regions.forEach(region => {
        const isSelected = region.id === selectedId;
        const icon = L.divIcon({
          className: '',
          html: `<div style="display:flex;align-items:center;gap:5px;"><div style="width:5px;height:5px;background:#722F37;border-radius:50%;border:1px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);flex-shrink:0;"></div><div class="region-pill${isSelected ? ' selected' : ''}" data-id="${region.id}">${region.name}</div></div>`,
          iconAnchor: [0, 10],
        });

        const marker = L.marker([region.lat, region.lng], { icon })
          .addTo(map)
          .on('click', () => onSelect(region.id));

        markersRef.current.push({ id: region.id, marker, L });
      });

      leafletMapRef.current = map;
    });

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        markersRef.current = [];
      }
    };
  }, []);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />;
}

export default function RegionSelector({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedRegion = regions.find(r => r.id === value) || regions[0];

  function handleSelect(regionId) {
    onChange(regionId);
    setIsOpen(false);
  }

  return (
    <>
      <style>{`
        .region-pill {
          background: rgba(114, 47, 55, 0.85);
          color: white;
          border: 1px solid rgba(255,255,255,0.35);
          border-radius: 20px;
          padding: 2px 5px;
          font-size: 8.5px;
          font-weight: 700;
          white-space: nowrap;
          box-shadow: 0 1px 4px rgba(0,0,0,0.3);
          cursor: pointer;
          font-family: -apple-system, BlinkMacSystemFont, sans-serif;
          letter-spacing: 0.1px;
          transition: background 0.15s, transform 0.15s;
        }
        .region-pill:hover {
          background: rgba(114, 47, 55, 1);
          transform: scale(1.05);
        }
        .region-pill.selected {
          background: #722F37;
          border-color: white;
          box-shadow: 0 0 0 2px white, 0 2px 10px rgba(0,0,0,0.4);
          animation: region-pulse 2s ease-out infinite;
        }
        @keyframes region-pulse {
          0%   { box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(201,162,39,0.8); }
          70%  { box-shadow: 0 0 0 2px white, 0 0 0 25px rgba(201,162,39,0); }
          100% { box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(201,162,39,0); }
        }
      `}</style>

      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "w-full flex items-center justify-between gap-3 p-3",
          "bg-white rounded-xl border border-stone-200",
          "hover:border-[#722F37]/30 transition-colors",
          isOpen && "ring-2 ring-[#722F37]/20 border-[#722F37]/30"
        )}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#722F37]/10 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-[#722F37]" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-stone-800">{selectedRegion.name}</p>
            <p className="text-sm text-stone-500">{selectedRegion.tagline}</p>
          </div>
        </div>
        <ChevronDown className="w-5 h-5 text-stone-400" />
      </button>

      {/* Map modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl overflow-hidden"
              style={{ height: '82vh' }}
            >
              <div className="bg-[#722F37] px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-white font-bold text-base">Choose Your Wine Region</p>
                  <p className="text-white/70 text-xs">Tap a region on the map</p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>

              <div style={{ height: 'calc(100% - 60px)' }}>
                <RegionMap selectedId={value} onSelect={handleSelect} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
